import {
  TextField,
  Grid,
  IconButton,
  Typography,
  Button,
  Chip,
  Container,
  Paper,
} from "@mui/material";
import { useFormContext, Controller, useFieldArray, useWatch } from "react-hook-form";
import { AddCircle, RemoveCircle } from "@mui/icons-material";
import { useState } from "react";

const defaultProcessSteps = [
  "Bearbeitung der Eingangspost",
  "Überweisungen",
  "Ablage im Buchhaltungsordner",
  "Beantwortung von Platzreservierungen und Reklamationen",
  "Rechnungsschreibung",
  "Kassenführung",
  "Personalverwaltung",
  "Annahme von Lieferungen mit Lieferpapieren",
  "Weitergabe buchhaltungsrelevanter Belege",
  "Schreiben von Stundenzetteln für die Lohnabrechnung",
  "Ausgabe von Bar-Rechnungen an Kunden",
  "Eintragungen ins Kassensystem",
  "Erstellung von Monatsabschlüssen",
  "Reklamationsbearbeitung",
  "Inventur",
  "Vertragsverwaltung",
  "Qualitätskontrollen",
];

export default function OrganisationForm() {
  const { control, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: "organizationUnits" });
  const [customStep, setCustomStep] = useState("");

  // Holt alle aktuellen ProzessSchritte für alle Felder
  const organizationUnits = useWatch({
    control,
    name: "organizationUnits",
  }) || [];

  const isStepSelected = (step, index) => {
    return Array.isArray(organizationUnits[index]?.processStep) && organizationUnits[index].processStep.includes(step);
  };

  const handleAddProcessStep = (step, index) => {
    if (fields.length > 0) {
      const current = Array.isArray(organizationUnits[index]?.processStep) ? organizationUnits[index].processStep : [];
      if (!current.includes(step)) {
        setValue(`organizationUnits.${index}.processStep`, [...current, step]);
      }
    }
  };

  const handleRemoveProcessStep = (step, index) => {
    const current = Array.isArray(organizationUnits[index]?.processStep) ? organizationUnits[index].processStep : [];
    setValue(`organizationUnits.${index}.processStep`, current.filter((s) => s !== step));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
          Organisationsstruktur
        </Typography>
        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Typography variant="body1" gutterBottom>
              Bitte geben Sie hier alle Organisationseinheiten an, in denen Belege anfallen, sowie die
              zugehörigen Prozessschritte. Beschreiben Sie möglichst konkret, welche Aufgaben innerhalb
              der Organisationseinheit mit Belegen verbunden sind. Beispiel:
              <br />
              <strong>Organisationseinheit:</strong> Büro
              <br />
              <strong>Prozess-Schritte:</strong> Bearbeitung der Eingangspost, Überweisungen,
              Ablage im Buchhaltungsordner, Bearbeitung von Reklamationen, Rechnungsschreibung,
              Kassenführung, Personalverwaltung etc.
            </Typography>
          </Grid>
          {fields.map((item, index) => (
            <Grid item xs={12} key={item.id}>
              <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
                <Grid container spacing={2} direction="column">
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Beschreibung Organisationseinheit
                    </Typography>
                    <Controller
                      name={`organizationUnits.${index}.unit`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Organisationseinheit" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Prozess-Schritte auswählen
                    </Typography>
                    <Grid container spacing={1}>
                      {defaultProcessSteps.map((step, i) => (
                        <Grid item key={i}>
                          <Chip
                            label={step}
                            clickable={!isStepSelected(step, index)}
                            color={isStepSelected(step, index) ? "default" : "primary"}
                            onClick={() => {
                              if (!isStepSelected(step, index)) {
                                handleAddProcessStep(step, index);
                              }
                            }}
                            disabled={isStepSelected(step, index)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Eigenen Prozess-Schritt hinzufügen
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={9}>
                        <TextField
                          label="Eigenen Prozess-Schritt"
                          fullWidth
                          value={customStep}
                          onChange={(e) => setCustomStep(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => {
                            if (customStep.trim() !== "") {
                              handleAddProcessStep(customStep.trim(), index);
                              setCustomStep("");
                            }
                          }}
                        >
                          Hinzufügen
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Ausgewählte Prozess-Schritte
                    </Typography>
                    <Controller
                      name={`organizationUnits.${index}.processStep`}
                      control={control}
                      defaultValue={[]}
                      render={({ field }) => (
                        <Grid container spacing={1}>
                          {(Array.isArray(field.value) ? field.value : []).map((step, i) => (
                            <Grid item key={i}>
                              <Chip
                                label={step}
                                color="primary"
                                onDelete={() => handleRemoveProcessStep(step, index)}
                              />
                            </Grid>
                          ))}
                          {(!field.value || field.value.length === 0) && (
                            <Typography variant="body2" color="text.secondary">
                              Noch keine Prozess-Schritte ausgewählt.
                            </Typography>
                          )}
                        </Grid>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ textAlign: "right" }}>
                    <IconButton color="error" onClick={() => remove(index)}>
                      <RemoveCircle />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button
              startIcon={<AddCircle />}
              variant="outlined"
              onClick={() => append({ unit: "", processStep: "" })}
            >
              Einheit hinzufügen
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Paper elevation={1} sx={{ p: 3, backgroundColor: "grey.50", borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Wichtiger Hinweis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          Die Angaben sollten vor der Verwendung von einem Steuerberater oder Rechtsanwalt geprüft werden. Wir übernehmen keine Haftung für die Richtigkeit oder Vollständigkeit der eingegebenen Inhalte.
        </Typography>
      </Paper>
    </Container>
  );
}
